import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';
import { EnvService } from '../services/env.service';
import { SharedService } from '../shared-service/shared.service';
import { EncrDecrService } from '../services/encrypt-decrypt.service';
import { hasTraversalPayload } from '../../sanitize-bootstrap-url';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  state = new BehaviorSubject<string>('');
  state$ = this.state.asObservable();
  isRefreshInProgress = false;

  constructor(
    private location: Location,
    private oauthService: OAuthService,
    private env: EnvService,
    private encDecSrvice: EncrDecrService,
    private sharedService: SharedService
  ) {
    if (!this.env.ssoUrl) {
    }

    // setup oauth
    this.oauthService.configure(this.env.oauthConfig());
    //this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events.subscribe({
      next: (event) => {
        // console.log("oauth event---", event);
        if (event.type === 'token_received') {
          window.localStorage.setItem('adata', this.encDecSrvice.set(this.oauthService.getAccessToken()));
          window.localStorage.setItem('rtoken', this.encDecSrvice.set(this.oauthService.getRefreshToken()));
          if (this.oauthService.state != '/') {
            // Persist the originally-requested deep link synchronously here (inside the
            // APP_INITIALIZER auth bootstrap) so app.component's redirectBasedOnRole()
            // can recover it. Otherwise it runs before callback.component restores the
            // path and falls back to the role's default home page.
            const decodedState = decodeURIComponent(this.oauthService.state);
            if (decodedState && decodedState !== '/' && !decodedState.startsWith('/callback')) {
              window.localStorage.setItem('path', decodedState);
            }
            this.state.next(this.oauthService.state);
          }
        }
        else if (event.type === "token_refresh_error") {
          window.localStorage.setItem('logout-event', 'logout')
          this.sharedService.logOut();
        }
      },
      error: (error) => {
        // console.log("oauth error---", error);
        window.localStorage.setItem('logout-event', 'logout')
        this.sharedService.logOut();
      }
    });
  }

  public isAuthenticated(): boolean {
    // console.log(this.oauthService.hasValidAccessToken())
    return this.oauthService.hasValidAccessToken() ? true : false;
  }

  async login(): Promise<boolean> {
    return this.oauthService.loadDiscoveryDocumentAndLogin().then(
      (isLoggedIn) => {
        if (isLoggedIn) {
          return true;
        } else {
          this._initCodeFlow();
          return false;
        }
      },
      (error) => {
        this._initCodeFlow();
        return false;
      }
    );
  }

  public logout(redirectTo = '/logout'): void {
    this.oauthService.logOut();
  }

  private _initCodeFlow(): void {
    const path = this.location.path() === '' ? '/' : this.location.path();
    // Never forward a raw path/query containing traversal sequences as OAuth state; it gets
    // reflected back verbatim in the SSO redirect otherwise.
    const state = hasTraversalPayload(path) ? '/' : path;
    this.oauthService.initCodeFlow(state);
  }

  initAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Skip auth for logout route.
      if (window.location.pathname === '/logout') {
        return resolve();
      }
      if (window.location.pathname === '/probe') {
        return resolve();
      }
      // Skip auth for no-role-external route so unauthenticated users can
      // view the SCW access request instructions without being redirected to login.
      if (window.location.pathname === '/no-role-external') {
        return resolve();
      }
      if (this.isAuthenticated()) {
        this.oauthService.loadDiscoveryDocumentAndTryLogin();
        return resolve();
      } else {
        return this.login()
          .then((isLoggedIn) => {
            if (isLoggedIn) {
              return this._getUserInfo(resolve, reject);
            }
          })
          .catch((error: any) => {
            window.document.body.classList.add('failed');
            return reject(error || 'server error');
          });
      }
    });
  }

  _getUserInfo(resolve, reject) {
    return resolve()
  }
}